import {
    useNavigate
} from 'react-router-dom';

import {useToggle} from '@mantine/hooks';


import {
    DeleteRecordsConfirmationButton,
} from "@flexinet/ui-components";
import { Message, MessageStatus } from '../types';
import { useState } from 'react';
import { ComposeEmailDrawer } from '../drawers/ComposeEmailDrawer';
import { ReplyButton } from '../buttons/ReplyButton';
import { ReplyAllButton } from '../buttons/ReplyAllButton';
import { ForwardButton } from '../buttons/ForwardButton';
import { MarkAsUnreadButton } from '../buttons/MarkAsUnreadButton';
import { RestoreButton } from '../buttons/RestoreButton';
import { CloseButton } from '../buttons/CloseButton';
import { ArchiveButton } from '../buttons/ArchiveButton';
import { UnarchiveButton } from '../buttons/UnarchiveButton';

export const useEditPageTools = (record?: Message, deleteRecord?: () => void, updateRecord?: (record: Message) => void) => {

    const navigate = useNavigate();

    const [mode] = useToggle(['view', 'edit']);
    const [replyDrawerOpened, setReplyDrawerOpened] = useState(false);
    const [replyAll, setReplyAll] = useState(false);
    const [forwardDrawerOpened, setForwardDrawerOpened] = useState(false);

    const tools = [];

    if (record) {
        tools.push(
            <ReplyButton 
                key={'reply'} 
                onClick={() => {
                    setReplyAll(false);
                    setReplyDrawerOpened(true);
                }}
            />
        );
        tools.push(
            <ReplyAllButton 
                key={'reply-all'} 
                onClick={() => {
                    setReplyAll(true);
                    setReplyDrawerOpened(true);
                }}
            />
        );
        tools.push(
            <ForwardButton 
                key={'forward'} 
                onClick={() => setForwardDrawerOpened(true)}
            />
        );
        
        if (record.status === MessageStatus.READ) {
            tools.push(
                <MarkAsUnreadButton 
                    key={'mark-unread'} 
                    record={record}
                    onClick={(updatedRecord) => {
                        if (updateRecord) {
                            updateRecord(updatedRecord);
                        }
                    }}
                />
            );
        }
        
        if (record.status === MessageStatus.DELETED) {
            tools.push(
                <RestoreButton 
                    key={'restore'} 
                    record={record}
                    onClick={(updatedRecord) => {
                        if (updateRecord) {
                            updateRecord(updatedRecord);
                        }
                    }}
                />
            );
        }
        
        if (record.status === MessageStatus.ARCHIVED) {
            tools.push(
                <UnarchiveButton 
                    key={'unarchive'} 
                    message={record}
                    onUnarchive={(updatedRecord) => {
                        if (updateRecord) {
                            updateRecord(updatedRecord);
                        }
                    }}
                />
            );
        }
        
        if (record.status !== MessageStatus.DELETED && record.status !== MessageStatus.ARCHIVED) {
            tools.push(
                <ArchiveButton 
                    key={'archive'} 
                    message={record}
                    onArchive={(updatedRecord) => {
                        if (updateRecord) {
                            updateRecord(updatedRecord);
                        }
                    }}
                />
            );
        }
        
        if (record.status !== MessageStatus.DELETED) {
            tools.push(
                <DeleteRecordsConfirmationButton
                    key={'delete'}
                    variant={'outline'}
                    handleConfirm={() => {
                        if (deleteRecord) {
                            deleteRecord();
                        }
                        navigate(getCloseUrl());
                    }}
                />
            );
        }
    }

    const getCloseUrl = () => {
        if (!record) return '/messages/status/inbox';
        if (record.status === MessageStatus.UNREAD) return '/messages/status/inbox';
        if (record.status === MessageStatus.READ) return '/messages/status/read';
        if (record.status === MessageStatus.SENT) return '/messages/status/sent';
        if (record.status === MessageStatus.DELETED) return '/messages/status/deleted';
        if (record.status === MessageStatus.ARCHIVED) return '/messages/status/archived';
        return '/messages/status/inbox';
    };

    tools.push(<CloseButton key={'close'} onClick={() => navigate(getCloseUrl())}/>);

    const drawer = record ? (
        <>
            <ComposeEmailDrawer
                opened={replyDrawerOpened}
                onClose={() => setReplyDrawerOpened(false)}
                replyTo={record}
                replyAll={replyAll}
            />
            <ComposeEmailDrawer
                opened={forwardDrawerOpened}
                onClose={() => setForwardDrawerOpened(false)}
                forwardMessage={record}
            />
        </>
    ) : null;

    return {
        mode,
        tools,
        drawer,
    };

};
